import { useState, useEffect, useCallback } from 'react';
import { Appearance, ColorSchemeName } from 'react-native';
import { lightTheme, darkTheme } from '../themes/design-system';
import { colorTokens } from '../tokens/colors';
import { spacingTokens } from '../tokens/spacing';
import { typographyTokens } from '../tokens/typography';
import { motionTokens } from '../tokens/motion';
import { tokenStorage } from '@coffeeplexity/shared';

// Theme preference storage key
const THEME_PREFERENCE_KEY = 'theme_preference';

export function useTheme() {
  const [colorScheme, setColorScheme] = useState<ColorSchemeName>(
    Appearance.getColorScheme()
  );
  const [userPreference, setUserPreference] = useState<ColorSchemeName | null>(
    null
  );
  const [isLoading, setIsLoading] = useState(true);

  // Load user preference on mount
  useEffect(() => {
    const loadUserPreference = async () => {
      try {
        const storedPreference =
          await tokenStorage.getToken(THEME_PREFERENCE_KEY);
        if (storedPreference) {
          setUserPreference(storedPreference as ColorSchemeName);
          setColorScheme(storedPreference as ColorSchemeName);
        }
      } catch (error) {
        console.warn('Failed to load theme preference:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadUserPreference();
  }, []);

  // Listen to system appearance changes only when user hasn't set a preference
  useEffect(() => {
    if (userPreference !== null) {
      // User has manually set a preference, don't listen to system changes
      return;
    }

    const subscription = Appearance.addChangeListener(({ colorScheme }) => {
      setColorScheme(colorScheme);
    });

    return () => subscription?.remove();
  }, [userPreference]);

  const isDark = colorScheme === 'dark';
  const baseTheme = isDark ? darkTheme : lightTheme;

  // Enhanced theme with new design tokens
  const enhancedTheme = {
    ...baseTheme,
    colors: {
      ...baseTheme.colors,
      // Theme-specific colors first
      ...(isDark ? colorTokens.themes.dark : colorTokens.themes.light),
      // Add new color tokens (these will override if there are conflicts)
      primary: colorTokens.primary,
      neutral: colorTokens.neutral,
      semantic: colorTokens.semantic,
    },
    spacing: {
      spacing: spacingTokens.spacing,
      borderRadius: spacingTokens.borderRadius,
      touchTargets: spacingTokens.touchTargets,
      elevation: spacingTokens.elevation,
    },
    typography: typographyTokens,
    motion: motionTokens,
  };

  // Toggle theme function that respects user preference
  const toggleTheme = useCallback(async () => {
    const newScheme = isDark ? 'light' : 'dark';

    try {
      // Store user preference
      await tokenStorage.setToken(THEME_PREFERENCE_KEY, newScheme);
      setUserPreference(newScheme);
      setColorScheme(newScheme);
    } catch (error) {
      console.error('Failed to save theme preference:', error);
      // Still update the UI even if storage fails
      setUserPreference(newScheme);
      setColorScheme(newScheme);
    }
  }, [isDark]);

  // Reset to system preference
  const resetToSystem = useCallback(async () => {
    try {
      await tokenStorage.removeToken(THEME_PREFERENCE_KEY);
      setUserPreference(null);
      setColorScheme(Appearance.getColorScheme());
    } catch (error) {
      console.error('Failed to reset theme preference:', error);
      setUserPreference(null);
      setColorScheme(Appearance.getColorScheme());
    }
  }, []);

  // Return the expected interface for backward compatibility
  return {
    theme: enhancedTheme,
    colorScheme,
    isDark,
    toggleTheme,
    resetToSystem,
    userPreference,
    isLoading,
  };
}

export function useColorScheme() {
  const [colorScheme, setColorScheme] = useState<ColorSchemeName>(
    Appearance.getColorScheme()
  );

  useEffect(() => {
    const subscription = Appearance.addChangeListener(({ colorScheme }) => {
      setColorScheme(colorScheme);
    });

    return () => subscription?.remove();
  }, []);

  return colorScheme;
}
